import { forwardRef, useId } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string | null;
  onClear?: () => void;
}

/**
 * Текстовое поле по макетам: floating-label сверху внутри рамки, оранжевая
 * рамка на фокусе/ошибке, опциональная кнопка очистки (×).
 *
 * Кнопка очистки спозиционирована абсолютно: в потоке она оказалась бы соседом
 * `<input>` и центрировалась по строке ввода, а не по всей высоте контрола —
 * с плавающей подписью сверху это заметно смещало её вниз (promt-86 §3.2).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, onClear, value, id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const hasValue = value !== undefined && value !== '';
    const showClear = Boolean(onClear) && hasValue;
    return (
      <div>
        <div
          className={cn(
            'relative flex h-[var(--control-h)] flex-col justify-center rounded-[var(--radius-control)] border bg-surface py-1 pl-3 transition-colors',
            showClear ? 'pr-10' : 'pr-3',
            error ? 'border-brand' : 'border-border-strong',
            className
          )}
        >
          {label && (
            <label
              htmlFor={inputId}
              className={cn('text-[11px] font-medium leading-4', error ? 'text-brand' : 'text-fg-muted')}
            >
              {label}
            </label>
          )}
          <input
            ref={ref}
            id={inputId}
            value={value}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${inputId}-err` : undefined}
            // pointer-coarse:text-base — 16px на тач-устройствах: иначе iOS Safari зумит
            // страницу при фокусе. Интерлиньяж поджимаем, иначе подпись + строка ввода
            // перестают помещаться в фиксированную высоту контрола (promt-86 §7).
            className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-muted pointer-coarse:text-base pointer-coarse:leading-5"
            {...props}
          />
          {showClear && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Очистить"
              className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-page hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {error && (
          <p id={`${inputId}-err`} className="mt-1 text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';
